import React from 'react';
import { Avatar } from '@vkontakte/vkui';
import { Icon24ChevronLeft, Icon24ChevronRight } from '@vkontakte/icons';
import './ProductImageCarousel.css';

function catalogMediaUrl(imagesUrl, imagePath, size) {
    const path = String(imagePath || '')
        .replace(/^https?:\/\/[^/]+/i, '')
        .replace(/\/{2,}/g, '/')
        .replace(/^\/+/, '');
    if (!path) {
        return '';
    }
    const base = String(imagesUrl || '').replace(/\/+$/, '');
    if (path.indexOf('assets2/img/') === 0 || path.indexOf('assets/images/') === 0) {
        return base + '/' + path;
    }
    if (size) {
        return base + '/image/' + size + '/' + size + '/' + path;
    }
    return base + '/' + path;
}

/**
 * Компонент карусели изображений товара
 * @param {string} productId - ID товара для группировки в Fancybox
 * @param {string} mainImage - URL главного изображения
 * @param {object} images - Объект с дополнительными изображениями
 * @param {string} imagesUrl - Базовый URL для изображений
 * @param {number} size - Размер аватара (по умолчанию 56)
 * @param {boolean} isMobile - Флаг мобильного устройства
 */
class ProductImageCarousel extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            currentIndex: 0,
            touchStart: null,
            touchEnd: null,
            swipeOffset: 0,
            isTransitioning: false
        };
    }

    /**
     * Получает массив всех изображений товара
     * @returns {Array} Массив URL изображений
     */
    getAllImages = () => {
        const { mainImage, images } = this.props;
        const allImages = [];
        
        // Добавляем главное изображение
        if (mainImage) {
            allImages.push(mainImage);
        }
        
        // Добавляем дополнительные изображения, исключая дубликаты
        if (images && typeof images === 'object') {
            Object.values(images).forEach(img => {
                if (img && !allImages.includes(img)) {
                    allImages.push(img);
                }
            });
        }
        
        return allImages;
    };

    /**
     * Переход к следующему изображению
     */
    nextImage = (e) => {
        e.stopPropagation();
        if (this.state.isTransitioning) return;
        
        const images = this.getAllImages();
        this.setState({ isTransitioning: true }, () => {
            this.setState(prevState => ({
                currentIndex: (prevState.currentIndex + 1) % images.length,
                swipeOffset: 0
            }), () => {
                setTimeout(() => this.setState({ isTransitioning: false }), 300);
            });
        });
    };

    /**
     * Переход к предыдущему изображению
     */
    prevImage = (e) => {
        e.stopPropagation();
        if (this.state.isTransitioning) return;
        
        const images = this.getAllImages();
        this.setState({ isTransitioning: true }, () => {
            this.setState(prevState => ({
                currentIndex: prevState.currentIndex === 0 
                    ? images.length - 1 
                    : prevState.currentIndex - 1,
                swipeOffset: 0
            }), () => {
                setTimeout(() => this.setState({ isTransitioning: false }), 300);
            });
        });
    };

    /**
     * Переход к конкретному изображению по индексу
     */
    goToImage = (index, e) => {
        e.stopPropagation();
        if (this.state.isTransitioning) return;
        
        this.setState({ 
            currentIndex: index,
            swipeOffset: 0,
            isTransitioning: true
        }, () => {
            setTimeout(() => this.setState({ isTransitioning: false }), 300);
        });
    };

    /**
     * Обработка начала касания
     */
    handleTouchStart = (e) => {
        if (this.state.isTransitioning) return;
        
        this.setState({ 
            touchEnd: null,
            touchStart: e.targetTouches[0].clientX,
            swipeOffset: 0
        });
    };

    /**
     * Обработка движения пальца
     */
    handleTouchMove = (e) => {
        if (!this.state.touchStart) return;
        
        const currentTouch = e.targetTouches[0].clientX;
        const offset = currentTouch - this.state.touchStart;
        
        // Плавное перемещение без ограничений (карусель циклическая)
        this.setState({ 
            touchEnd: currentTouch,
            swipeOffset: offset
        });
    };

    /**
     * Обработка окончания касания
     */
    handleTouchEnd = (e) => {
        if (!this.state.touchStart || !this.state.touchEnd) {
            this.setState({ swipeOffset: 0 });
            return;
        }
        
        const distance = this.state.touchStart - this.state.touchEnd;
        const minSwipeDistance = 50;
        const images = this.getAllImages();
        const { size = 56 } = this.props;
        
        this.setState({ isTransitioning: true });
        
        if (Math.abs(distance) > minSwipeDistance) {
            if (distance > 0) {
                // Свайп влево - следующее изображение
                // Сначала доезжаем до конца влево (-size)
                this.setState({ 
                    swipeOffset: -size,
                    touchStart: null,
                    touchEnd: null
                }, () => {
                    // После завершения анимации меняем индекс и сбрасываем offset
                    setTimeout(() => {
                        this.setState(prevState => ({
                            currentIndex: (prevState.currentIndex + 1) % images.length,
                            swipeOffset: 0,
                            isTransitioning: false
                        }));
                    }, 300);
                });
            } else {
                // Свайп вправо - предыдущее изображение
                // Сначала доезжаем до конца вправо (+size)
                this.setState({ 
                    swipeOffset: size,
                    touchStart: null,
                    touchEnd: null
                }, () => {
                    // После завершения анимации меняем индекс и сбрасываем offset
                    setTimeout(() => {
                        this.setState(prevState => ({
                            currentIndex: prevState.currentIndex === 0 
                                ? images.length - 1 
                                : prevState.currentIndex - 1,
                            swipeOffset: 0,
                            isTransitioning: false
                        }));
                    }, 300);
                });
            }
        } else {
            // Свайп недостаточный - возврат в исходное положение
            this.setState({ 
                swipeOffset: 0,
                touchStart: null,
                touchEnd: null
            }, () => {
                setTimeout(() => this.setState({ isTransitioning: false }), 300);
            });
        }
    };

    render() {
        const { productId, imagesUrl, size = 56, isMobile = false } = this.props;
        const { currentIndex, swipeOffset, isTransitioning } = this.state;
        const allImages = this.getAllImages();
        const hasMultipleImages = allImages.length > 1;

        // Получаем предыдущее, текущее и следующее изображение
        const prevIndex = currentIndex === 0 ? allImages.length - 1 : currentIndex - 1;
        const nextIndex = (currentIndex + 1) % allImages.length;
        
        const prevImage = allImages[prevIndex];
        const currentImage = allImages[currentIndex];
        const nextImage = allImages[nextIndex];

        // Стили для контейнера с изображениями (слайдер)
        // Если несколько изображений - сдвигаем на -size чтобы показать центральное (текущее)
        // Если одно изображение - не сдвигаем
        const baseOffset = hasMultipleImages ? -size : 0;
        const sliderStyle = {
            transform: `translateX(${baseOffset + swipeOffset}px)`,
            transition: isTransitioning ? 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)' : 'none',
            willChange: 'transform',
            display: 'flex',
            alignItems: 'center'
        };

        // Стили для контейнера - ограничиваем ширину размером одного изображения
        const containerStyle = {
            width: `${size}px`,
            height: `${size}px`
        };

        // Стиль для каждого слайда
        const slideStyle = {
            width: `${size}px`,
            height: `${size}px`
        };

        return (
            <div 
                className="product-image-carousel"
                style={containerStyle}
                onTouchStart={isMobile && hasMultipleImages ? this.handleTouchStart : undefined}
                onTouchMove={isMobile && hasMultipleImages ? this.handleTouchMove : undefined}
                onTouchEnd={isMobile && hasMultipleImages ? this.handleTouchEnd : undefined}
            >
                <div className="product-image-carousel__slider" style={sliderStyle}>
                    {/* Предыдущее изображение */}
                    {hasMultipleImages && (
                        <div className="product-image-carousel__slide" style={slideStyle}>
                            <Avatar 
                                mode="image" 
                                style={{ borderRadius: '5%' }}
                                size={size}
                                src={catalogMediaUrl(imagesUrl, prevImage, size)}
                            />
                        </div>
                    )}
                    
                    {/* Текущее изображение */}
                    <div className="product-image-carousel__slide" style={slideStyle}>
                        <a 
                            className="product-image-carousel__main"
                            data-fancybox={`gallery${productId}`}
                            href={catalogMediaUrl(imagesUrl, currentImage, 1500)}
                            data-carousel-index={currentIndex}
                        >
                            <Avatar 
                                mode="image" 
                                style={{ borderRadius: '5%' }}
                                size={size}
                                src={catalogMediaUrl(imagesUrl, currentImage, size)}
                            />
                        </a>
                    </div>
                    
                    {/* Следующее изображение */}
                    {hasMultipleImages && (
                        <div className="product-image-carousel__slide" style={slideStyle}>
                            <Avatar 
                                mode="image" 
                                style={{ borderRadius: '5%' }}
                                size={size}
                                src={catalogMediaUrl(imagesUrl, nextImage, size)}
                            />
                        </div>
                    )}
                </div>

                {/* Скрытые изображения для Fancybox */}
                <div style={{ display: 'none' }}>
                    {allImages.map((img, index) => (
                        index !== currentIndex && (
                            <a 
                                key={`fancybox-${productId}-${index}`}
                                data-fancybox={`gallery${productId}`}
                                href={catalogMediaUrl(imagesUrl, img, 1500)}
                            />
                        )
                    ))}
                </div>

                {/* Кнопки навигации (только на десктопе) */}
                {hasMultipleImages && !isMobile && (
                    <>
                        <button 
                            className="product-image-carousel__nav product-image-carousel__nav--prev"
                            onClick={this.prevImage}
                            aria-label="Предыдущее изображение"
                        >
                            <Icon24ChevronLeft />
                        </button>
                        <button 
                            className="product-image-carousel__nav product-image-carousel__nav--next"
                            onClick={this.nextImage}
                            aria-label="Следующее изображение"
                        >
                            <Icon24ChevronRight />
                        </button>
                    </>
                )}

                {/* Индикаторы (dots) */}
                {hasMultipleImages && (
                    <div className="product-image-carousel__dots">
                        {allImages.map((_, index) => (
                            <button
                                key={`dot-${productId}-${index}`}
                                className={`product-image-carousel__dot ${
                                    index === currentIndex ? 'product-image-carousel__dot--active' : ''
                                }`}
                                onClick={(e) => this.goToImage(index, e)}
                                aria-label={`Изображение ${index + 1} из ${allImages.length}`}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }
}

export default ProductImageCarousel;

